import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "@/lib/api";
import { D, designError, label, useDesignMe } from "@/lib/designApi";
import { DataTable, NoAccess, PageHeader, Section, Stat, StatusBadge } from "@/components/design/common";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

// Fixed categorical order (validated reference palette, slots 1-3) — a
// series keeps its colour no matter which others are shown.
const SERIES = { releases: "#2a78d6", revisions: "#eb6834", ecns: "#1baf7a" };
const AXIS = { fontSize: 11, fill: "#64748b" };
const ENTITY_LABEL = { items: "Items", documents: "Drawings / Docs", boms: "BOMs", routings: "Routings", standards: "Standards" };

function ChartTooltip({ active, payload, label: l }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm px-3 py-2 text-xs">
      <div className="font-semibold text-slate-800 mb-1">{l}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-slate-700">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />
          {p.name}: <b>{p.value}</b>
        </div>
      ))}
    </div>
  );
}

export default function DesignDashboard() {
  const { me, error: meErr } = useDesignMe();
  const [d, setD] = useState(null);
  const [queue, setQueue] = useState([]);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  const load = () => {
    api.get(`${D}/dashboard`).then((r) => setD(r.data)).catch((e) => setErr(designError(e)));
    api.get(`${D}/approvals/queue`).then((r) => setQueue(r.data)).catch(() => setQueue([]));
  };
  useEffect(load, []);

  if (meErr || (me && !me.has_access)) return <NoAccess error={meErr} />;
  if (err) return <NoAccess error={err} />;
  if (!d) return <div className="text-sm text-slate-500">Loading…</div>;
  const k = d.kpis;

  return (
    <div className="space-y-6 max-md:space-y-3" data-testid="design-dashboard">
      <PageHeader title="Design Dashboard" subtitle={`Signed in as ${me?.functional_role_label || ""} · workload, ageing, approvals, revisions and ECNs`}>
        <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1.5" />Refresh</Button>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 max-md:gap-2">
        <Stat label="My queue" value={k.my_queue} tone="text-blue-700" onClick={() => nav("/design/approvals")} testid="kpi-my-queue" />
        <Stat label="Open requests" value={k.open_requests} hint={`${k.unassigned_requests} unassigned`} onClick={() => nav("/design/requests")} />
        <Stat label="Overdue requests" value={k.overdue_requests} tone={k.overdue_requests ? "text-red-600" : "text-slate-900"} />
        <Stat label="Pending check" value={k.pending_check} tone="text-amber-600" />
        <Stat label="Pending approval" value={k.pending_approve} tone="text-amber-600" />
        <Stat label="Approved · to release" value={k.pending_release} tone="text-sky-700" />
        <Stat label="Avg approval cycle" value={k.avg_approval_cycle_days != null ? `${k.avg_approval_cycle_days} d` : "—"} hint="submit → approve, 90 days" />
        <Stat label="Released items" value={k.released_items} tone="text-emerald-700" />
        <Stat label="Released drawings" value={k.released_drawings} tone="text-emerald-700" />
        <Stat label="Open ECNs" value={k.open_ecns} hint={`${k.open_ecrs} open ECRs`} onClick={() => nav("/design/changes")} />
        <Stat label="Avg ECN lead time" value={k.avg_ecn_lead_days != null ? `${k.avg_ecn_lead_days} d` : "—"} hint="raise → implement" />
        <Stat label="Active NPD projects" value={k.active_npd} onClick={() => nav("/design/npd")} hint={k.on_time_completion_pct != null ? `${k.on_time_completion_pct}% requests on time` : undefined} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 max-md:gap-3">
        <Section title="Releases, revisions & ECNs — last 12 months" className="lg:col-span-2">
          <div className="h-64 max-md:h-52" data-testid="design-monthly-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.monthly} barGap={2} barCategoryGap="22%">
                <CartesianGrid vertical={false} stroke="#eef2f7" />
                <XAxis dataKey="month" tick={AXIS} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
                <YAxis allowDecimals={false} tick={AXIS} tickLine={false} axisLine={false} width={28} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "#f1f5f9" }} />
                <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: 12, color: "#475569" }} />
                <Bar dataKey="releases" name="Releases" fill={SERIES.releases} radius={[4, 4, 0, 0]} maxBarSize={14} />
                <Bar dataKey="revisions" name="New revisions" fill={SERIES.revisions} radius={[4, 4, 0, 0]} maxBarSize={14} />
                <Bar dataKey="ecns" name="ECNs raised" fill={SERIES.ecns} radius={[4, 4, 0, 0]} maxBarSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section title="Open request ageing (days)">
          <div className="h-64 max-md:h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.request_ageing}>
                <CartesianGrid vertical={false} stroke="#eef2f7" />
                <XAxis dataKey="bucket" tick={AXIS} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
                <YAxis allowDecimals={false} tick={AXIS} tickLine={false} axisLine={false} width={28} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "#f1f5f9" }} />
                <Bar dataKey="count" name="Requests" fill={SERIES.releases} radius={[4, 4, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 max-md:gap-3">
        <Section title="Design workload (open requests)">
          <DataTable rows={d.workload} empty="No open requests."
            columns={[
              { key: "name", title: "Engineer", render: (w) => w.name },
              { key: "open", title: "Open", cellClass: "mono" },
              { key: "in_progress", title: "In progress", cellClass: "mono" },
              { key: "urgent", title: "High / urgent", cellClass: "mono" },
              { key: "overdue", title: "Overdue", render: (w) => <span className={w.overdue ? "text-red-600 font-semibold" : ""}>{w.overdue}</span> },
            ]} />
        </Section>
        <Section title="Records by status">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b border-slate-100">
                  <th className="py-2 pr-3">Record</th>
                  {["draft", "under_review", "approved", "released", "superseded", "obsolete"].map((s) => (
                    <th key={s} className="py-2 px-2">{label(s)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(d.status_counts).map(([e, c]) => (
                  <tr key={e} className="border-b border-slate-50">
                    <td className="py-2 pr-3 font-medium">{ENTITY_LABEL[e] || e}</td>
                    {["draft", "under_review", "approved", "released", "superseded", "obsolete"].map((s) => (
                      <td key={s} className="py-2 px-2 mono text-slate-700">{c[s] || 0}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-xs text-slate-500">
            Review ageing (days in review): {d.review_ageing.map((b) => `${b.bucket}: ${b.count}`).join(" · ")}
          </div>
        </Section>
      </div>

      <Section title={`My queue (${queue.length})`} right={<Button size="sm" variant="outline" onClick={() => nav("/design/approvals")}>Open queue</Button>}>
        <DataTable rows={queue.slice(0, 8)} empty="Nothing waiting on you."
          onRowClick={(r) => nav(queueLink(r))}
          columns={[
            { key: "step", title: "Action", render: (r) => <span className="uppercase text-[11px] font-semibold text-blue-700">{r.step}</span> },
            { key: "entity_label", title: "Type" },
            { key: "number", title: "Number", render: (r) => <span className="mono">{r.number}{r.revision ? ` Rev ${r.revision}` : ""}</span> },
            { key: "title", title: "Title", cellClass: "max-w-xs truncate" },
            { key: "status", title: "Status", render: (r) => <StatusBadge status={r.status} /> },
            { key: "age_days", title: "Age (d)", cellClass: "mono" },
          ]} />
      </Section>
    </div>
  );
}

export function queueLink(r) {
  const base = { items: "items", documents: "documents", boms: "boms", routings: "routings", standards: "standards",
    requests: "requests", npd: "npd" }[r.entity];
  if (r.entity === "ecns") return `/design/changes?ecn=${r.id}`;
  if (r.entity === "ecrs") return `/design/changes?ecr=${r.id}`;
  return `/design/${base}?id=${r.id}`;
}
